import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { LoadingOverlay } from '@/components/ui'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { OnboardingGate } from '@/components/layout/OnboardingGate'
import { LegalAcceptanceGate } from '@/components/layout/LegalAcceptanceGate'
import { NoPlanGate } from '@/components/layout/NoPlanGate'
import { ContractGate } from '@/components/layout/ContractGate'
import { installBrowserSwUpdateReload } from '@/lib/swUpdateReload'
import AdminLayout from '@/components/layout/AdminLayout'
import InstructorLayout from '@/components/layout/InstructorLayout'
// Import estatico (componente trivial, sem payload proprio): e ele que preserva a query
// string das duas URLs antigas da r6-29.
import { LegacyAnalysisRedirect } from '@/components/features/analysis/LegacyAnalysisRedirect'
// r6-29, R5: o caminho canonico da secao (e o das duas URLs antigas) sai de uma constante
// so. `ANALYSIS_ROUTE_SEGMENTS` e a mesma constante na grafia relativa que as filhas de
// `/app` exigem, derivada la — o literal aqui era uma segunda grafia do mesmo caminho.
import { ANALYSIS_ROUTE_SEGMENTS } from '@/lib/copy/analysis'
// r6-28, R5: o endereco da tela de Registro de Estudos sai de uma constante so. O alvo do
// redirect da rota antiga le daqui para nao envelhecer se o caminho mudar de novo.
import { MANUAL_ROUTE } from '@/lib/copy/manual'

// Lazy load pages
const LoginPage = lazy(() => import('@/pages/Login'))
// r7-19/r7-25: checkout da plataforma (`/checkout/<slug>?cupom=`), publico e sem menu.
const CheckoutPage = lazy(() => import('@/pages/Checkout'))
const CheckoutSuccessPage = lazy(() => import('@/pages/CheckoutSuccess'))
// VENDA-03: pagina publica do link de venda (`/pagar/<token>`), sem menu.
const SalesLinkPayPage = lazy(() => import('@/pages/SalesLinkPay'))

// Formulario de entrada 2.0 (onboarding) — autenticado, sem DashboardLayout
const OnboardingPage = lazy(() => import('@/pages/Onboarding'))
// LGPD-05: aceite dos documentos legais, antes do onboarding
const LegalAcceptancePage = lazy(() => import('@/pages/LegalAcceptance'))
// r7-25: "nenhum plano vinculado a sua conta" — autenticada, sem menu
const NoPlanPage = lazy(() => import('@/pages/NoPlan'))
// R12: "Assine seu contrato" — fora do DashboardLayout, como o aceite e o onboarding
const ContractPage = lazy(() => import('@/pages/Contract'))

// Documentos legais: unicas paginas do antigo site institucional que seguem publicas,
// porque login, tela de aceite e Configuracoes linkam para elas (LGPD).
const TermsOfUsePage = lazy(() => import('@/pages/TermsOfUse'))
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicy'))

// Dashboard pages
const DashboardPage = lazy(() => import('@/pages/Dashboard'))
const QuizzesPage = lazy(() => import('@/pages/Quizzes/index'))
const TakeQuizPage = lazy(() => import('@/pages/Quizzes/TakeQuiz'))
const QuizResultPage = lazy(() => import('@/pages/Quizzes/QuizResult'))
const PerformancePage = lazy(() => import('@/pages/Performance'))
// r6-29: /app/analise-progresso e /app/guia-estatistico viraram abas desta pagina.
const StatisticalAnalysisPage = lazy(() => import('@/pages/StatisticalAnalysis'))
const StudyJourneyPage = lazy(() => import('@/pages/StudyJourney'))
const IntegrationsPage = lazy(() => import('@/pages/Integrations'))
const PrimaryStudiesPage = lazy(() => import('@/pages/PrimaryStudies'))
const RevisionsPage = lazy(() => import('@/pages/Revisions'))
const SimuladosMedwayPage = lazy(() => import('@/pages/Simulados'))
const RecommendedRevisionsPage = lazy(() => import('@/pages/RecommendedRevisions'))
const PrevalencePage = lazy(() => import('@/pages/Prevalence'))
const ExamsPage = lazy(() => import('@/pages/Exams'))
// Spec r2/02: o comparativo entre provas. Rota IRMA de `provas`, e nao filha de
// `provas/:attemptId` — a tela nao e sobre uma tentativa, e sobre uma selecao.
const ExamComparisonPage = lazy(() => import('@/pages/ExamComparison'))
const TakeExamPage = lazy(() => import('@/pages/TakeExam'))
const ExamAnswersPage = lazy(() => import('@/pages/ExamAnswers'))
const ManualEntriesPage = lazy(() => import('@/pages/ManualEntries'))
const CalendarPage = lazy(() => import('@/pages/Calendar'))
const SettingsPage = lazy(() => import('@/pages/Settings'))
// r5-06: meta TED da semana em dois passos (disponivel -> meta)
const TedGoalPage = lazy(() => import('@/pages/TedGoal'))
// R08-22: parametros do ciclo do proprio aluno (Modo Essential: o aluno sem padrinho seta)
const CycleParametersPage = lazy(() => import('@/pages/CycleParameters'))
const InitialDataLoadPage = lazy(() => import('@/pages/InitialDataLoad'))
const SubscriptionsPage = lazy(() => import('@/pages/Subscriptions'))
const VideoAulasPage = lazy(() => import('@/pages/VideoAulas'))
const VideoCoursePage = lazy(() => import('@/pages/VideoCourse'))
// ZOOM: aulas ao vivo. Lista e sala servem aluno, instrutor e (a sala) o admin.
const LiveSessionsPage = lazy(() => import('@/pages/LiveSessions'))
const LiveSessionRoomPage = lazy(() => import('@/pages/LiveSessionRoom'))
// HERMES: processos seletivos do aluno (instituicoes-alvo + exames nacionais).
const ExamProcessesPage = lazy(() => import('@/pages/ExamProcesses'))
// Spec 05, R4: o formulario recorrente do dia 25. Sem item de menu — fora da janela ele
// nao existe na tela, e um link permanente contradiria isso.
const DifficultyReportPage = lazy(() => import('@/pages/DifficultyReport'))

// Admin pages
const AdminDashboardPage = lazy(() => import('@/pages/admin/Dashboard'))
const AdminUsersPage = lazy(() => import('@/pages/admin/Users'))
const AdminUserDetailPage = lazy(() => import('@/pages/admin/UserDetail'))
const AdminSubscriptionsPage = lazy(() => import('@/pages/admin/Subscriptions'))
const AdminAnalyticsPage = lazy(() => import('@/pages/admin/Analytics'))
const AdminMentorshipsPage = lazy(() => import('@/pages/admin/Mentorships'))
const AdminRevenuePage = lazy(() => import('@/pages/admin/Revenue'))
const AdminQuizzesPage = lazy(() => import('@/pages/admin/Quizzes'))
const AdminCoursesPage = lazy(() => import('@/pages/admin/Courses'))
const AdminCouponsPage = lazy(() => import('@/pages/admin/Coupons'))
// VENDA-02: links de venda do comercial.
const AdminSalesLinksPage = lazy(() => import('@/pages/admin/SalesLinks'))
const AdminSalesLinkNewPage = lazy(() => import('@/pages/admin/SalesLinkNew'))
const AdminSalesLinkDetailPage = lazy(() => import('@/pages/admin/SalesLinkDetail'))
// r7-20/r7-25: planos do checkout (AsaasPlan) — lista e pagina de criar/editar
const AdminPlansPage = lazy(() => import('@/pages/admin/Plans'))
const AdminPlanEditorPage = lazy(() => import('@/pages/admin/PlanEditor'))
const AdminAnnouncementsPage = lazy(() => import('@/pages/admin/Announcements'))
// LGPD-06: documentos legais (rascunho, publicar, historico)
const AdminLegalDocumentsPage = lazy(() => import('@/pages/admin/LegalDocuments'))
const AdminLegalDocumentEditorPage = lazy(() => import('@/pages/admin/LegalDocumentEditor'))
const AdminPrepCoursesPage = lazy(() => import('@/pages/admin/PrepCourses'))
const AdminCourseLessonsPage = lazy(() => import('@/pages/admin/CourseLessons'))
const AdminCourseLessonBulkLinksPage = lazy(() => import('@/pages/admin/CourseLessonBulkLinks'))
const AdminProvasIntegraPage = lazy(() => import('@/pages/admin/ProvasIntegra'))
const AdminProvaIntegraQuestionsPage = lazy(() => import('@/pages/admin/ProvaIntegraQuestions'))
const AdminInstitutionsPage = lazy(() => import('@/pages/admin/Institutions'))
// R12-07: faculdades de graduacao (lista do aluno), separadas das bancas.
const AdminGraduationInstitutionsPage = lazy(() => import('@/pages/admin/GraduationInstitutions'))
const AdminSpecialtiesPage = lazy(() => import('@/pages/admin/Specialties'))
const AdminSelectionProcessesPage = lazy(() => import('@/pages/admin/SelectionProcesses'))
// Catalogos > Objetivos (r6-23): o que o aluno quer ser, por processo seletivo.
const AdminCareerGoalsPage = lazy(() => import('@/pages/admin/CareerGoals'))
// Subespecialidades e Topicos sao a mesma tela em dois niveis do catalogo — a UI e
// identica; so muda a tabela por tras (`sub_specialties` / `topics`).
const AdminCatalogLevelPage = lazy(() => import('@/pages/admin/CatalogLevel'))
const AdminCatalogCorrectionRequestsPage = lazy(() => import('@/pages/admin/CatalogCorrectionRequests'))
// R10-20: pedidos de prova dos alunos (Provas na Integra > Pedidos de prova).
const AdminExamRequestsPage = lazy(() => import('@/pages/admin/ExamRequests'))
const AdminExamDemandPage = lazy(() => import('@/pages/admin/ExamDemand'))
const AdminLessonTopicAdjustmentsPage = lazy(() => import('@/pages/admin/LessonTopicAdjustments'))
// R08-26: calendario editorial de provas (prazos do edital por instituicao).
const AdminExamDeadlinesPage = lazy(() => import('@/pages/admin/ExamDeadlines'))
// HERMES: processos seletivos (fila de revisoes do agente + cadastro manual).
const AdminExamProcessesPage = lazy(() => import('@/pages/admin/ExamProcesses'))
const AdminApiKeysPage = lazy(() => import('@/pages/admin/ApiKeys'))
// R10-15: perfis de acesso do admin por secao.
const AdminProfilesPage = lazy(() => import('@/pages/admin/AdminProfiles'))
// R10-14: pacotes de features do instrutor.
const AdminInstructorPackagesPage = lazy(() => import('@/pages/admin/InstructorPackages'))
const AdminAssistantSettingsPage = lazy(() => import('@/pages/admin/AssistantSettings'))
const AdminZoomSettingsPage = lazy(() => import('@/pages/admin/ZoomSettings'))
// TRANSC-04/16: configuracao do Transcritor, painel do piloto e leitura de transcricao pelo admin.
const AdminTranscriberSettingsPage = lazy(() => import('@/pages/admin/TranscriberSettings'))
// R12-02: contrato do aluno (Autentique).
const AdminStudentContractSettingsPage = lazy(() => import('@/pages/admin/StudentContractSettings'))
const AdminTranscriberPilotPage = lazy(() => import('@/pages/admin/TranscriberPilot'))
const AdminMeetingTranscriptPage = lazy(() => import('@/pages/admin/MeetingTranscript'))
// R10-24: metricas das 1:1 por madrinha.
const AdminMeetingMetricsPage = lazy(() => import('@/pages/admin/MeetingMetrics'))
const AdminFollowupVideosPage = lazy(() => import('@/pages/admin/FollowupVideos'))
const AdminVideoCoursesPage = lazy(() => import('@/pages/admin/VideoCourses'))
const AdminVideoCourseDetailPage = lazy(() => import('@/pages/admin/VideoCourseDetail'))
// Spec 05, R4: onde o texto livre do relato mensal e lido por um humano.
const AdminDifficultyReportsPage = lazy(() => import('@/pages/admin/DifficultyReports'))
const AdminLiveSessionsPage = lazy(() => import('@/pages/admin/LiveSessions'))
const AdminLiveSessionDetailPage = lazy(() => import('@/pages/admin/LiveSessionDetail'))

// Instructor pages
const InstructorDashboardPage = lazy(() => import('@/pages/instructor/Dashboard'))
const InstructorStudentsPage = lazy(() => import('@/pages/instructor/Students'))
const InstructorStudentDetailPage = lazy(() => import('@/pages/instructor/StudentDetail'))
const InstructorMeetingModePage = lazy(() => import('@/pages/instructor/MeetingMode'))
const InstructorMeetingBriefingPage = lazy(() => import('@/pages/instructor/MeetingBriefing'))
const InstructorMeetingTranscriptPage = lazy(() => import('@/pages/instructor/MeetingTranscript'))
const InstructorProfilePage = lazy(() => import('@/pages/instructor/Profile'))
const InstructorAdministrativePage = lazy(() => import('@/pages/instructor/Administrative'))
const InstructorQualityMirrorPage = lazy(() => import('@/pages/instructor/QualityMirror'))
// INST-06: Central do Aprovado (editais e datas das instituicoes-alvo da carteira).
const InstructorApprovalCenterPage = lazy(() => import('@/pages/instructor/ApprovalCenter'))
// MH9-12: relatorio do ciclo do mes da carteira.
const InstructorCycleReportPage = lazy(() => import('@/pages/instructor/CycleReport'))
// IA-08: assistente de IA do instrutor (POC).
const InstructorAssistantPage = lazy(() => import('@/pages/instructor/Assistant'))
// TRANSC-14: download do app desktop Transcritor.
const InstructorTranscriberPage = lazy(() => import('@/pages/instructor/Transcriber'))

// Social pages
// const FriendsPage = lazy(() => import('@/pages/social/Friends')) // hidden: renamed to Match Hunter
const MatchHunterPage = lazy(() => import('@/pages/social/MatchHunter'))

// New menu pages
const MetricsAnalysisPage = lazy(() => import('@/pages/MetricsAnalysis'))
const MetricsAreaDetailPage = lazy(() => import('@/pages/MetricsAreaDetail'))
// StatisticalGuidePage saiu na r6-29 (virou a aba "Guia Estatistico" de
// StatisticalAnalysisPage, declarada acima).
const ChatLayoutPage = lazy(() => import('@/pages/social/ChatLayout'))

// Loading wrapper
function PageLoader({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<LoadingOverlay />}>
      {children}
    </Suspense>
  )
}

// Router configuration
const router = createBrowserRouter([
  // Sem site institucional: a raiz cai no login, que manda quem ja esta logado ao painel.
  {
    path: '/',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/termos-e-condicoes',
    element: (
      <PageLoader>
        <TermsOfUsePage />
      </PageLoader>
    ),
  },
  {
    path: '/politica-de-privacidade',
    element: (
      <PageLoader>
        <PrivacyPolicyPage />
      </PageLoader>
    ),
  },
  {
    path: '/checkout',
    element: (
      <PageLoader>
        <CheckoutPage />
      </PageLoader>
    ),
  },
  // r7-25: URL amigavel `/checkout/<slug>`. `/checkout/sucesso` e rota estatica (ganha do
  // parametro) e o slug `sucesso` e reservado no backend.
  {
    path: '/checkout/:slug',
    element: (
      <PageLoader>
        <CheckoutPage />
      </PageLoader>
    ),
  },
  {
    path: '/checkout/sucesso',
    element: (
      <PageLoader>
        <CheckoutSuccessPage />
      </PageLoader>
    ),
  },
  // VENDA-03: link de venda de uso unico. Publico, sem layout; o token e a credencial.
  {
    path: '/pagar/:token',
    element: (
      <PageLoader>
        <SalesLinkPayPage />
      </PageLoader>
    ),
  },
  // Auth routes (no layout)
  {
    path: '/login',
    element: (
      <PageLoader>
        <LoginPage />
      </PageLoader>
    ),
  },
  // Onboarding (formulario de entrada 2.0) — rota autenticada com casco proprio.
  // Fica fora do DashboardLayout de proposito: uma rota dentro de /app/* daria porta de
  // fuga pelo menu lateral. O OnboardingGate faz a autenticacao e a Guarda 2.
  // LGPD-05: fora do DashboardLayout, como o onboarding; o gate faz a autenticacao.
  {
    path: '/aceite',
    element: <LegalAcceptanceGate />,
    children: [
      {
        index: true,
        element: (
          <PageLoader>
            <LegalAcceptancePage />
          </PageLoader>
        ),
      },
    ],
  },
  // r7-25: tela "sem plano", fora do DashboardLayout como o aceite e o onboarding.
  {
    path: '/sem-plano',
    element: <NoPlanGate />,
    children: [
      {
        index: true,
        element: (
          <PageLoader>
            <NoPlanPage />
          </PageLoader>
        ),
      },
    ],
  },
  // R12: contrato a assinar fecha a plataforma; fora do DashboardLayout, sem menu.
  {
    path: '/contrato',
    element: <ContractGate />,
    children: [
      {
        index: true,
        element: (
          <PageLoader>
            <ContractPage />
          </PageLoader>
        ),
      },
    ],
  },
  {
    path: '/onboarding',
    element: <OnboardingGate />,
    children: [
      {
        index: true,
        element: (
          <PageLoader>
            <OnboardingPage />
          </PageLoader>
        ),
      },
    ],
  },
  // Protected routes with DashboardLayout
  {
    path: '/app',
    element: <DashboardLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/app/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <PageLoader>
            <DashboardPage />
          </PageLoader>
        ),
      },
      {
        path: 'simulados',
        element: (
          <PageLoader>
            <QuizzesPage />
          </PageLoader>
        ),
      },
      {
        path: 'simulados/:id',
        element: (
          <PageLoader>
            <TakeQuizPage />
          </PageLoader>
        ),
      },
      {
        path: 'simulados/:id/resultado',
        element: (
          <PageLoader>
            <QuizResultPage />
          </PageLoader>
        ),
      },
      {
        path: 'desempenho',
        element: (
          <PageLoader>
            <PerformancePage />
          </PageLoader>
        ),
      },
      {
        path: ANALYSIS_ROUTE_SEGMENTS.progress,
        // r6-29 (R1): URL antiga viva por redirect, com a query preservada — ha bookmark
        // e link em conversa antiga com `?period=`, `?status=`, `?specialty=`, `?q=` e
        // `?aberto=`. Nao remover sem substituir esses links.
        element: <LegacyAnalysisRedirect tab="progresso" />,
      },
      {
        path: 'parametros',
        element: (
          <PageLoader>
            <CycleParametersPage />
          </PageLoader>
        ),
      },
      {
        path: 'jornada',
        element: (
          <PageLoader>
            <StudyJourneyPage />
          </PageLoader>
        ),
      },
      {
        path: 'integracoes',
        element: (
          <PageLoader>
            <IntegrationsPage />
          </PageLoader>
        ),
      },
      {
        path: 'estudos',
        element: (
          <PageLoader>
            <PrimaryStudiesPage />
          </PageLoader>
        ),
      },
      {
        path: 'revisoes',
        element: (
          <PageLoader>
            <RevisionsPage />
          </PageLoader>
        ),
      },
      {
        path: 'simulados-integracoes',
        element: (
          <PageLoader>
            <SimuladosMedwayPage />
          </PageLoader>
        ),
      },
      {
        path: 'prevalencia',
        element: (
          <PageLoader>
            <PrevalencePage />
          </PageLoader>
        ),
      },
      {
        path: 'provas',
        element: (
          <PageLoader>
            <ExamsPage />
          </PageLoader>
        ),
      },
      {
        // ANTES de `provas/:attemptId`: o React Router v6 ja ranqueia segmento
        // estatico acima de dinamico, mas a ordem explicita evita que alguem leia
        // as duas rotas como ambiguas e mova uma sem perceber.
        path: 'provas/comparar',
        element: (
          <PageLoader>
            <ExamComparisonPage />
          </PageLoader>
        ),
      },
      {
        path: 'provas/:attemptId',
        element: (
          <PageLoader>
            <TakeExamPage />
          </PageLoader>
        ),
      },
      {
        // A prova na integra corrigida ("Ver respostas" do resultado, spec r4/10). Rota
        // propria, e nao flag na de cima: "voltar" do navegador sai da correcao e cai no
        // resultado.
        path: 'provas/:attemptId/respostas',
        element: (
          <PageLoader>
            <ExamAnswersPage />
          </PageLoader>
        ),
      },
      {
        path: 'registro-estudos',
        element: (
          <PageLoader>
            <ManualEntriesPage />
          </PageLoader>
        ),
      },
      // r6-28, R3: /app/manual virou /app/registro-estudos. O caminho antigo continua
      // respondendo porque ha push ja entregue no dispositivo do aluno com ele no
      // payload (e e-mail e link antigo). `replace` para o "voltar" do navegador nao
      // cair de volta na rota morta. A tela nao le query string, entao nao ha o que
      // preservar no redirect. O `path` continua literal porque caminho de rota filha e
      // relativo a '/app'; o ALVO vem de `MANUAL_ROUTE` (R5).
      { path: 'manual', element: <Navigate to={MANUAL_ROUTE} replace /> },
      {
        path: 'revisoes-recomendadas',
        element: (
          <PageLoader>
            <RecommendedRevisionsPage />
          </PageLoader>
        ),
      },
      {
        path: 'video-aulas',
        element: (
          <PageLoader>
            <VideoAulasPage />
          </PageLoader>
        ),
      },
      // Um curso de videoaulas (spec r4/12): player + aulas, na ordem do curso.
      {
        path: 'video-aulas/:courseId',
        element: (
          <PageLoader>
            <VideoCoursePage />
          </PageLoader>
        ),
      },
      // ZOOM: aulas ao vivo (lista + sala com o Meeting SDK).
      {
        path: 'aulas-ao-vivo',
        element: (
          <PageLoader>
            <LiveSessionsPage basePath="/app/aulas-ao-vivo" />
          </PageLoader>
        ),
      },
      {
        path: 'aulas-ao-vivo/:id',
        element: (
          <PageLoader>
            <LiveSessionRoomPage backPath="/app/aulas-ao-vivo" />
          </PageLoader>
        ),
      },
      // HERMES: processos seletivos (.spec/hermes/contrato.md §6).
      {
        path: 'processos-seletivos',
        element: (
          <PageLoader>
            <ExamProcessesPage />
          </PageLoader>
        ),
      },
      {
        path: 'relato-de-dificuldades',
        element: (
          <PageLoader>
            <DifficultyReportPage />
          </PageLoader>
        ),
      },
      {
        path: 'calendario',
        element: (
          <PageLoader>
            <CalendarPage />
          </PageLoader>
        ),
      },
      {
        path: 'assinatura',
        element: (
          <PageLoader>
            <SubscriptionsPage />
          </PageLoader>
        ),
      },
      {
        path: 'configuracoes',
        element: (
          <PageLoader>
            <SettingsPage />
          </PageLoader>
        ),
      },
      {
        path: 'meta-ted',
        element: (
          <PageLoader>
            <TedGoalPage />
          </PageLoader>
        ),
      },
      // Primeira carga de dados. Fica dentro de /app (e nao em /onboarding) porque o
      // requisito exige alcanca-la a qualquer momento, inclusive por Configuracoes,
      // depois do formulario de entrada concluido.
      {
        path: 'primeira-carga',
        element: (
          <PageLoader>
            <InitialDataLoadPage />
          </PageLoader>
        ),
      },
      {
        path: 'analise-metricas',
        element: (
          <PageLoader>
            <MetricsAnalysisPage />
          </PageLoader>
        ),
      },
      {
        path: 'analise-metricas/:areaCode',
        element: (
          <PageLoader>
            <MetricsAreaDetailPage />
          </PageLoader>
        ),
      },
      {
        // r6-29: a fusao. O nome e o caminho canonico moram em `ANALYSIS_SECTION`
        // (src/lib/copy/analysis.ts); aqui entra o mesmo caminho na grafia relativa que
        // a filha de `/app` exige, derivada la — nao um literal paralelo.
        path: ANALYSIS_ROUTE_SEGMENTS.section,
        element: (
          <PageLoader>
            <StatisticalAnalysisPage />
          </PageLoader>
        ),
      },
      {
        path: ANALYSIS_ROUTE_SEGMENTS.guide,
        // r6-29 (R1): URL antiga viva por redirect, como a de analise-progresso.
        element: <LegacyAnalysisRedirect tab="guia" />,
      },
      // Social routes
      // { path: 'social/friends', ... } // hidden: renamed to Match Hunter
      {
        path: 'social/match-hunter',
        element: (
          <PageLoader>
            <MatchHunterPage />
          </PageLoader>
        ),
      },
      {
        path: 'social/friends',
        element: <Navigate to="/app/social/match-hunter" replace />,
      },
      {
        path: 'social/conversations',
        element: (
          <PageLoader>
            <ChatLayoutPage />
          </PageLoader>
        ),
      },
      {
        path: 'social/chat/:id',
        element: (
          <PageLoader>
            <ChatLayoutPage />
          </PageLoader>
        ),
      },
    ],
  },
  // Admin routes
  {
    path: '/admin',
    element: <AdminLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/admin/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <PageLoader>
            <AdminDashboardPage />
          </PageLoader>
        ),
      },
      {
        path: 'users',
        element: (
          <PageLoader>
            <AdminUsersPage />
          </PageLoader>
        ),
      },
      {
        path: 'users/:id',
        element: (
          <PageLoader>
            <AdminUserDetailPage />
          </PageLoader>
        ),
      },
      {
        path: 'subscriptions',
        element: (
          <PageLoader>
            <AdminSubscriptionsPage />
          </PageLoader>
        ),
      },
      {
        path: 'analytics',
        element: (
          <PageLoader>
            <AdminAnalyticsPage />
          </PageLoader>
        ),
      },
      {
        path: 'revenue',
        element: (
          <PageLoader>
            <AdminRevenuePage />
          </PageLoader>
        ),
      },
      {
        path: 'mentorships',
        element: (
          <PageLoader>
            <AdminMentorshipsPage />
          </PageLoader>
        ),
      },
      // R10-24: metricas das 1:1 por madrinha (secao mentorias, pelo prefixo /admin/mentorships).
      {
        path: 'mentorships/metricas',
        element: (
          <PageLoader>
            <AdminMeetingMetricsPage />
          </PageLoader>
        ),
      },
      {
        path: 'quizzes',
        element: (
          <PageLoader>
            <AdminQuizzesPage />
          </PageLoader>
        ),
      },
      {
        path: 'courses',
        element: (
          <PageLoader>
            <AdminCoursesPage />
          </PageLoader>
        ),
      },
      {
        path: 'prep-courses',
        element: (
          <PageLoader>
            <AdminPrepCoursesPage />
          </PageLoader>
        ),
      },
      {
        path: 'followup-videos',
        element: (
          <PageLoader>
            <AdminFollowupVideosPage />
          </PageLoader>
        ),
      },
      // Videoaulas de curso (spec r4/12): curso + aulas do Vimeo com condicao de
      // liberacao. Nao e /admin/courses (marketplace legado) nem followup-videos.
      {
        path: 'video-courses',
        element: (
          <PageLoader>
            <AdminVideoCoursesPage />
          </PageLoader>
        ),
      },
      {
        path: 'video-courses/:id',
        element: (
          <PageLoader>
            <AdminVideoCourseDetailPage />
          </PageLoader>
        ),
      },
      {
        path: 'relatos-de-dificuldade',
        element: (
          <PageLoader>
            <AdminDifficultyReportsPage />
          </PageLoader>
        ),
      },
      // ZOOM: aulas ao vivo. A sala do admin (host) e a mesma pagina do aluno/instrutor.
      {
        path: 'aulas-ao-vivo',
        element: (
          <PageLoader>
            <AdminLiveSessionsPage />
          </PageLoader>
        ),
      },
      {
        path: 'aulas-ao-vivo/:id',
        element: (
          <PageLoader>
            <AdminLiveSessionDetailPage />
          </PageLoader>
        ),
      },
      {
        path: 'aulas-ao-vivo/:id/sala',
        element: (
          <PageLoader>
            <LiveSessionRoomPage backPath="/admin/aulas-ao-vivo" />
          </PageLoader>
        ),
      },
      {
        path: 'course-lessons',
        element: (
          <PageLoader>
            <AdminCourseLessonsPage />
          </PageLoader>
        ),
      },
      {
        // r6-32: vinculo em lote aula -> subespecialidade/topico.
        path: 'course-lessons/vincular',
        element: (
          <PageLoader>
            <AdminCourseLessonBulkLinksPage />
          </PageLoader>
        ),
      },
      {
        path: 'provas-integra',
        element: (
          <PageLoader>
            <AdminProvasIntegraPage />
          </PageLoader>
        ),
      },
      {
        path: 'provas-integra/questoes',
        element: (
          <PageLoader>
            <AdminProvaIntegraQuestionsPage />
          </PageLoader>
        ),
      },
      {
        path: 'provas-integra/pedidos',
        element: (
          <PageLoader>
            <AdminExamRequestsPage />
          </PageLoader>
        ),
      },
      {
        path: 'provas-integra/demanda',
        element: (
          <PageLoader>
            <AdminExamDemandPage />
          </PageLoader>
        ),
      },
      {
        path: 'institutions',
        element: (
          <PageLoader>
            <AdminInstitutionsPage />
          </PageLoader>
        ),
      },
      {
        path: 'graduation-institutions',
        element: (
          <PageLoader>
            <AdminGraduationInstitutionsPage />
          </PageLoader>
        ),
      },
      {
        path: 'specialties',
        element: (
          <PageLoader>
            <AdminSpecialtiesPage />
          </PageLoader>
        ),
      },
      {
        path: 'selection-processes',
        element: (
          <PageLoader>
            <AdminSelectionProcessesPage />
          </PageLoader>
        ),
      },
      {
        path: 'career-goals',
        element: (
          <PageLoader>
            <AdminCareerGoalsPage />
          </PageLoader>
        ),
      },
      // O caminho da SPA sempre falou o vocabulario de produto, mesmo quando a tabela
      // por tras chamava este nivel de `study_theme` — por isso a URL nao muda aqui e
      // links salvos por admins continuam valendo.
      {
        path: 'sub-specialties',
        element: (
          <PageLoader>
            <AdminCatalogLevelPage
              level="sub_specialties"
              title="Subespecialidades"
              subtitle="Nível intermediário do catálogo, sob uma grande área"
              parentLabel="Grande área"
              parentHint="a grande área a que a subespecialidade pertence"
              childLabel="tópicos"
            />
          </PageLoader>
        ),
      },
      // Rota antiga da mesma tela, de quando ela se chamava "Temas".
      { path: 'themes', element: <Navigate to="/admin/sub-specialties" replace /> },
      // R5: fila de aulas/topicos que alunos informaram e o catalogo nao tem. Fica
      // perto das telas de cadastro porque e nelas que a solicitacao e resolvida.
      {
        path: 'catalog-corrections',
        element: (
          <PageLoader>
            <AdminCatalogCorrectionRequestsPage />
          </PageLoader>
        ),
      },
      // Topicos que alunos tiraram/puseram numa aula ao registrar o estudo — insumo para
      // reavaliar o vinculo aula -> topicos em Aulas de Cursinho.
      {
        path: 'lesson-topic-adjustments',
        element: (
          <PageLoader>
            <AdminLessonTopicAdjustmentsPage />
          </PageLoader>
        ),
      },
      {
        path: 'exam-deadlines',
        element: (
          <PageLoader>
            <AdminExamDeadlinesPage />
          </PageLoader>
        ),
      },
      // HERMES: processos seletivos — fila de revisoes do agente e cadastro manual.
      {
        path: 'exam-processes',
        element: (
          <PageLoader>
            <AdminExamProcessesPage />
          </PageLoader>
        ),
      },
      // R10-14: pacotes de features do instrutor (Basico, Zoom, Coordenacao de madrinhas...).
      {
        path: 'pacotes-do-instrutor',
        element: (
          <PageLoader>
            <AdminInstructorPackagesPage />
          </PageLoader>
        ),
      },
      // R10-15: perfis de acesso do admin (so admin sem perfil; ver lib/adminSections).
      {
        path: 'configuracoes/perfis-de-acesso',
        element: (
          <PageLoader>
            <AdminProfilesPage />
          </PageLoader>
        ),
      },
      // MH9-01: chaves de API dos agentes externos.
      {
        path: 'chaves-de-api',
        element: (
          <PageLoader>
            <AdminApiKeysPage />
          </PageLoader>
        ),
      },
      // MH9-13: assistente de IA — liberacao por padrinho e config versionada.
      {
        path: 'assistente-ia',
        element: (
          <PageLoader>
            <AdminAssistantSettingsPage />
          </PageLoader>
        ),
      },
      // ZOOM: credenciais da Zoom no admin (substitui o .env).
      {
        path: 'configuracoes/zoom',
        element: (
          <PageLoader>
            <AdminZoomSettingsPage />
          </PageLoader>
        ),
      },
      // R12-02: contratos do aluno (PDFs por plano) e chave da emissao (Autentique).
      {
        path: 'configuracoes/contrato-do-aluno',
        element: (
          <PageLoader>
            <AdminStudentContractSettingsPage />
          </PageLoader>
        ),
      },
      // TRANSC-04: provedor/modelo de transcricao, chaves e versao do app desktop.
      {
        path: 'configuracoes/transcritor',
        element: (
          <PageLoader>
            <AdminTranscriberSettingsPage />
          </PageLoader>
        ),
      },
      // TRANSC-16: painel do piloto (adesao por instrutor, falhas, custo/hora).
      {
        path: 'configuracoes/transcritor/piloto',
        element: (
          <PageLoader>
            <AdminTranscriberPilotPage />
          </PageLoader>
        ),
      },
      // TRANSC-16: transcricao de qualquer 1:1, lida pelo admin (D9).
      {
        path: 'transcricoes/:meetingId',
        element: (
          <PageLoader>
            <AdminMeetingTranscriptPage />
          </PageLoader>
        ),
      },
      // /admin/topics e o Topico do CATALOGO (`topics`), nao o `canonical_topics` do
      // FSRS — que nunca teve tela. A rota de API homonima trocou de dono junto.
      {
        path: 'topics',
        element: (
          <PageLoader>
            <AdminCatalogLevelPage
              level="topics"
              title="Tópicos"
              subtitle="O que o aluno escolhe no autocomplete de estudo"
              parentLabel="Subespecialidade"
              parentHint="a subespecialidade a que o tópico pertence"
              childLabel="subtópicos"
            />
          </PageLoader>
        ),
      },
      {
        path: 'coupons',
        element: (
          <PageLoader>
            <AdminCouponsPage />
          </PageLoader>
        ),
      },
      {
        path: 'links-de-venda',
        element: (
          <PageLoader>
            <AdminSalesLinksPage />
          </PageLoader>
        ),
      },
      {
        path: 'links-de-venda/novo',
        element: (
          <PageLoader>
            <AdminSalesLinkNewPage />
          </PageLoader>
        ),
      },
      {
        path: 'links-de-venda/:id',
        element: (
          <PageLoader>
            <AdminSalesLinkDetailPage />
          </PageLoader>
        ),
      },
      // r7-25: "Produtos" virou "Planos", com pagina dedicada de criar/editar.
      {
        path: 'planos',
        element: (
          <PageLoader>
            <AdminPlansPage />
          </PageLoader>
        ),
      },
      {
        path: 'planos/novo',
        element: (
          <PageLoader>
            <AdminPlanEditorPage />
          </PageLoader>
        ),
      },
      {
        path: 'planos/:id',
        element: (
          <PageLoader>
            <AdminPlanEditorPage />
          </PageLoader>
        ),
      },
      { path: 'products', element: <Navigate to="/admin/planos" replace /> },
      {
        path: 'announcements',
        element: (
          <PageLoader>
            <AdminAnnouncementsPage />
          </PageLoader>
        ),
      },
      {
        path: 'documentos-legais',
        element: (
          <PageLoader>
            <AdminLegalDocumentsPage />
          </PageLoader>
        ),
      },
      {
        path: 'documentos-legais/:id',
        element: (
          <PageLoader>
            <AdminLegalDocumentEditorPage />
          </PageLoader>
        ),
      },
    ],
  },
  // Instructor routes
  {
    path: '/instructor',
    element: <InstructorLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/instructor/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <PageLoader>
            <InstructorDashboardPage />
          </PageLoader>
        ),
      },
      {
        path: 'students',
        element: (
          <PageLoader>
            <InstructorStudentsPage />
          </PageLoader>
        ),
      },
      {
        path: 'students/:id',
        element: (
          <PageLoader>
            <InstructorStudentDetailPage />
          </PageLoader>
        ),
      },
      {
        // R08-12: modo reuniao de uma 1:1 (ata, compromissos e condutas da reuniao).
        path: 'students/:id/meetings/:meetingId',
        element: (
          <PageLoader>
            <InstructorMeetingModePage />
          </PageLoader>
        ),
      },
      {
        // R08-15: briefing pre-reuniao da 1:1 (pauta gerada + secoes do §2).
        path: 'students/:id/meetings/:meetingId/briefing',
        element: (
          <PageLoader>
            <InstructorMeetingBriefingPage />
          </PageLoader>
        ),
      },
      {
        // TRANSC-08: transcricao da 1:1 gravada pelo Transcritor (Instrutor / Aluno).
        path: 'students/:id/meetings/:meetingId/transcript',
        element: (
          <PageLoader>
            <InstructorMeetingTranscriptPage />
          </PageLoader>
        ),
      },
      {
        path: 'profile',
        element: (
          <PageLoader>
            <InstructorProfilePage />
          </PageLoader>
        ),
      },
      {
        // MH9-11: o preco e definido pela equipe (ActiveAdmin); a tela saiu do menu e o link
        // antigo cai no dashboard. A pagina fica no repo para o marketplace de mentorias.
        path: 'pricing',
        element: <Navigate to="/instructor/dashboard" replace />,
      },
      {
        // R08-25: "Ganhos" virou "Administrativa"; o link antigo redireciona.
        path: 'earnings',
        element: <Navigate to="/instructor/administrativa" replace />,
      },
      {
        path: 'administrativa',
        element: (
          <PageLoader>
            <InstructorAdministrativePage />
          </PageLoader>
        ),
      },
      {
        // INST-06: Central do Aprovado.
        path: 'central-do-aprovado',
        element: (
          <PageLoader>
            <InstructorApprovalCenterPage />
          </PageLoader>
        ),
      },
      {
        // MH9-12: relatorio do ciclo do mes da carteira.
        path: 'relatorio-do-ciclo',
        element: (
          <PageLoader>
            <InstructorCycleReportPage />
          </PageLoader>
        ),
      },
      {
        // R08-24: espelho de qualidade do proprio padrinho.
        path: 'espelho',
        element: (
          <PageLoader>
            <InstructorQualityMirrorPage />
          </PageLoader>
        ),
      },
      {
        path: 'assistente',
        element: (
          <PageLoader>
            <InstructorAssistantPage />
          </PageLoader>
        ),
      },
      // TRANSC-14: download do MedHunter Transcritor (macOS / Windows) e instrucoes.
      {
        path: 'transcritor',
        element: (
          <PageLoader>
            <InstructorTranscriberPage />
          </PageLoader>
        ),
      },
      // ZOOM: aulas ao vivo (lista + sala com o Meeting SDK).
      {
        path: 'aulas-ao-vivo',
        element: (
          <PageLoader>
            <LiveSessionsPage basePath="/instructor/aulas-ao-vivo" showPastSessions />
          </PageLoader>
        ),
      },
      {
        path: 'aulas-ao-vivo/:id',
        element: (
          <PageLoader>
            <LiveSessionRoomPage backPath="/instructor/aulas-ao-vivo" />
          </PageLoader>
        ),
      },
    ],
  },
  // Redirects for old paths (backward compatibility)
  {
    path: '/dashboard',
    element: <Navigate to="/app/dashboard" replace />,
  },
  {
    path: '*',
    element: <Navigate to="/login" replace />,
  },
])

// Depois de um deploy, a primeira navegacao carrega o bundle novo (ver swUpdateReload.ts).
const swUpdate = installBrowserSwUpdateReload()
let lastPathname = router.state.location.pathname
router.subscribe((state) => {
  if (state.location.pathname === lastPathname) return
  lastPathname = state.location.pathname
  swUpdate.onNavigation()
})

export function AppRouter() {
  return <RouterProvider router={router} />
}
